import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { toast } from 'sonner';
import bs58 from 'bs58';
import axios from 'axios';
import { assertAdapterIdentity, walletDebugLog, walletAdapterForensics, walletProviderForensics } from '../lib/walletIdentity';
import { isAutoSiwsSuppressedForPath } from '../lib/autoSiwsPolicy';

/*
 * Phase 1 auth context.
 *
 * Flow:
 *   1. Wallet connects → we call POST /api/auth/nonce with the pubkey.
 *   2. Server returns a fresh single-use nonce + issued/expires timestamps.
 *   3. We build the canonical sign-in message (domain-bound + wallet-bound
 *      + nonce-bound + time-bound) and ask the wallet to sign it.
 *   4. We POST /api/auth/verify with the exact message + signature. The
 *      server verifies both and sets an HttpOnly cookie via Set-Cookie.
 *   5. All subsequent /api/* calls send the cookie automatically thanks
 *      to `withCredentials: true`. `authenticatedWallet` is derived from
 *      the server's /api/auth/session probe, not from client-side state.
 *
 * Session persistence: because the browser owns the HttpOnly cookie,
 * we don't manage tokens in JS. On page load we call /api/auth/session;
 * if it 200s we're already signed in from a prior tab/day.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Production origin — used in the SIWS domain field so a phishing
// site with a different domain cannot reuse a signature captured
// here. Kept in code (not env) so a rogue env override cannot silently
// downgrade the wallet-signing prompt.
const APP_DOMAIN = 'degensdao.com';
const APP_STATEMENT =
  'Sign in to Degens DAO. This signature does not authorize any transaction.';

// Ensure every request from anywhere in the app sends the session cookie.
// Same-origin in this deployment, so this is a no-op on other axios
// instances that use fetch(); those hit /api and pick up the cookie
// automatically anyway. This line just aligns the default axios instance.
axios.defaults.withCredentials = true;

const WalletAuthContext = createContext({
  isAuthenticated: false,
  authenticatedWallet: null,
  isVerifying: false,
  entitlements: null,
  capabilities: [],
  benefits: null,
  qualifyingAssets: [],
  refreshEntitlements: async () => {},
  signOut: async () => {},
});

export const useWalletAuth = () => useContext(WalletAuthContext);

// RTL-WALLET-LIFECYCLE (2026-08-24) — Route-scoped suppression list
// for the global Mission-Control SIWS auto-effect lives in a plain
// JS module (`autoSiwsPolicy.js`) so it is directly Node-runnable
// for regression tests. See that module's comment block for the
// full rationale.

export const WalletAuthProvider = ({ children }) => {
  const { connected, publicKey, signMessage, disconnect, wallet, wallets, select } = useWallet();
  const [authenticatedWallet, setAuthenticatedWallet] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [entitlements, setEntitlements] = useState(null);
  const [capabilities, setCapabilities] = useState([]);
  const [benefits, setBenefits] = useState(null);
  const [qualifyingAssets, setQualifyingAssets] = useState([]);
  const verifyingRef = useRef(false);
  const lastPromptedWallet = useRef(null);

  const walletAddress = publicKey?.toBase58() || null;
  const isAuthenticated =
    connected && !!walletAddress && authenticatedWallet === walletAddress;

  // RTL-WALLET-SELECTOR-2 (2026-08-24) — mount-time diagnostic dump.
  // When `?wallet-debug=1` is present in the URL, log every wallet the
  // browser is exposing to the app (name, url, icon prefix, readyState)
  // plus the currently-selected adapter. Gives operators concrete
  // evidence of Backpack-shim registrations at runtime WITHOUT logging
  // pubkeys, signatures, or message contents. No-op when the query
  // param is absent — production traffic sees nothing.
  useEffect(() => {
    walletDebugLog('mount', { wallets, wallet });
  }, [wallets, wallet]);

  /*
   * Build the canonical sign-in message. Must match the server's parser
   * in `_parse_signin_message` (routes/auth.py). Field order matters.
   */
  const buildSignInMessage = ({ wallet, nonce, issuedAt, expiresAt }) => (
    `Degens DAO wants you to sign in with your Solana account:\n` +
    `${wallet}\n\n` +
    `Domain: ${APP_DOMAIN}\n` +
    `URI: ${window.location.origin}\n` +
    `Version: 1\n` +
    `Chain: solana:mainnet\n` +
    `Nonce: ${nonce}\n` +
    `Issued At: ${issuedAt}\n` +
    `Expires At: ${expiresAt}\n` +
    `Statement: ${APP_STATEMENT}`
  );

  const loadEntitlements = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/me/entitlements`, {
        withCredentials: true,
        validateStatus: (s) => s === 200 || s === 401,
      });
      if (res.status === 200) {
        setEntitlements(res.data?.entitlements || {});
        setCapabilities(res.data?.capabilities || []);
        setBenefits(res.data?.benefits || {});
        setQualifyingAssets(res.data?.qualifying_assets || []);
      }
    } catch {
      /* ignore — presentational only */
    }
  }, []);

  const refreshEntitlements = useCallback(async () => {
    try {
      const res = await axios.post(
        `${API}/me/refresh-entitlements`,
        {},
        { withCredentials: true, validateStatus: (s) => s === 200 || s === 429 }
      );
      if (res.status === 200) {
        setEntitlements(res.data?.entitlements || {});
        setCapabilities(res.data?.capabilities || []);
        setBenefits(res.data?.benefits || {});
        setQualifyingAssets(res.data?.qualifying_assets || []);
        toast.success('Entitlements refreshed');
      } else if (res.status === 429) {
        const retry = res.data?.detail?.retry_after || 60;
        toast.info(`Please wait ${retry}s before refreshing again.`);
      }
    } catch {
      toast.error('Refresh failed');
    }
  }, []);

  /*
   * On mount / whenever the wallet changes, ask the server whether we
   * already have a live session cookie for this wallet. If yes, skip
   * the signature prompt. This is what lets returning users refresh
   * the tab without re-signing.
   */
  const probeExistingSession = useCallback(async (address) => {
    try {
      const res = await axios.get(`${API}/auth/session`, {
        withCredentials: true,
        validateStatus: (s) => s === 200 || s === 401,
      });
      if (res.status === 200 && res.data?.wallet_address === address) {
        setAuthenticatedWallet(address);
        // Load presentational entitlements — the auth guards on
        // sensitive endpoints re-check independently server-side.
        loadEntitlements();
        return true;
      }
    } catch {
      /* ignore — treat as no session */
    }
    return false;
  }, [loadEntitlements]);

  const requestSignature = useCallback(
    async (address) => {
      if (verifyingRef.current) return;
      verifyingRef.current = true;
      setIsVerifying(true);

      try {
        // RTL-WALLET-SWITCH (2026-08-24) — atomic wallet-identity
        // check. When the operator disconnects one wallet and picks
        // another, wallet-adapter-react can briefly hold a state where
        // `signMessage` and `publicKey` are bound to DIFFERENT
        // adapters (previous provider's signer + new provider's
        // pubkey, or vice-versa). Signing would then pop up the wrong
        // wallet AND the signature would be invalid (address ≠ signer).
        // Read the currently-selected adapter directly and verify its
        // pubkey matches the address we were called with. If not,
        // abort — never fall back to a different provider.
        const adapter = wallet?.adapter;
        const adapterAddress = adapter?.publicKey?.toBase58() || null;
        if (!adapter || adapterAddress !== address) {
          throw new Error(
            'WALLET_PROVIDER_MISMATCH: the connected wallet changed. Please reconnect and try again.'
          );
        }
        // RTL-WALLET-SELECTOR-2 (2026-08-24) — identity guard.
        // Backpack can register a Wallet Standard entry whose
        // `name === "Phantom"` shim so clicking "Phantom" in the modal
        // actually pops Backpack for signing. Pubkey pinning alone is
        // insufficient because a user who imported the same seed into
        // both wallets sees the same pubkey on both adapters. The
        // `url` field is baked into each wallet's own Wallet Standard
        // registration — Backpack cannot proxy its url to
        // `phantom.app`. Assert identity BEFORE the nonce request and
        // again AFTER the round-trip (see below) so a Backpack shim
        // cannot silently reach the sign step.
        walletDebugLog('siws-pre-nonce', wallet);
        // RTL-WALLET-SELECTOR-2 forensics (2026-08-24) — dump the
        // EXACT pinned adapter variable that is about to be handed
        // to the identity guard. Previous walletDebugLog was passed
        // the destructured `wallet` (adapter-react `Wallet | null`),
        // NOT the outer context, so it reported `currently selected:
        // null` while the guard simultaneously received a live
        // "Phantom" adapter. This call removes that ambiguity —
        // whatever this dump reports is what the guard sees.
        walletAdapterForensics('siws-before-assert', adapter, {
          expectedAddress: address,
          step: 'pre-nonce identity assertion',
        });
        assertAdapterIdentity(adapter);

        const adapterSignMessage = adapter.signMessage?.bind(adapter);
        if (typeof adapterSignMessage !== 'function') {
          throw new Error(
            'WALLET_PROVIDER_MISMATCH: current wallet does not support message signing.'
          );
        }

        // Step 1 — nonce
        const nonceRes = await axios.post(
          `${API}/auth/nonce`,
          { wallet_address: address },
          { withCredentials: true }
        );
        const { nonce, issued_at, expires_at } = nonceRes.data;

        // Re-check adapter identity AFTER the nonce round-trip. A
        // slow network can leave a window where the operator swaps
        // wallets between nonce request and signing.
        const adapterAddressPostNonce = wallet?.adapter?.publicKey?.toBase58() || null;
        if (adapterAddressPostNonce !== address) {
          throw new Error(
            'WALLET_PROVIDER_MISMATCH: the connected wallet changed during sign-in. Please reconnect and try again.'
          );
        }
        // RTL-WALLET-SELECTOR-2 — re-run the URL/identity assertion
        // after the network round-trip. Window between pre-check and
        // signature is the exact race a Backpack shim could exploit
        // (adapter reference swap during Promise resolution).
        walletDebugLog('siws-post-nonce', wallet);
        walletAdapterForensics('siws-post-nonce-before-assert', wallet?.adapter, {
          expectedAddress: address,
          step: 'post-nonce identity assertion',
        });
        assertAdapterIdentity(wallet?.adapter);

        // Step 2 — build + sign (using the pinned adapter method, not
        // the destructured `signMessage` closure which can lag by a
        // render across wallet swaps).
        const message = buildSignInMessage({
          wallet: address,
          nonce,
          issuedAt: issued_at,
          expiresAt: expires_at,
        });
        const encoded = new TextEncoder().encode(message);
        // RTL-WALLET-SELECTOR-2 forensics — dump the EXACT adapter
        // whose signMessage is about to be invoked. If Phantom is
        // selected but Backpack opens, the underlying-wallet metadata
        // here must reveal it: iconFingerprint + underlyingWallet.name
        // + vendorFeatureKeys are all baked into the extension's own
        // Wallet Standard registration.
        walletAdapterForensics('siws-before-sign', adapter, {
          expectedAddress: address,
          step: 'invoking adapter.signMessage',
          adapterSignMessageFnName: adapterSignMessage.name || null,
        });
        // Invocation-layer forensics — dumps the actual Wallet
        // Standard feature function that will run inside
        // StandardWalletAdapter._signMessage, plus the vendor
        // namespace providers (window.solana / window.phantom.solana
        // / window.backpack). This is the layer BELOW adapter
        // selection where the failure must live.
        walletProviderForensics('siws-before-sign', adapter, {
          expectedAddress: address,
          step: 'invocation layer probe',
        });
        const signature = await adapterSignMessage(encoded);
        const signatureBase58 = bs58.encode(signature);

        // Step 3 — verify (server sets the HttpOnly session cookie)
        await axios.post(
          `${API}/auth/verify`,
          {
            wallet_address: address,
            message,
            signature: signatureBase58,
          },
          { withCredentials: true }
        );

        setAuthenticatedWallet(address);
        toast.success('Wallet verified!');
        loadEntitlements();
      } catch (err) {
        console.error('Wallet verification failed:', err);
        const detail = err?.response?.data?.detail;
        const rawMessage = err?.message || '';
        let msg;
        if (rawMessage.startsWith('WALLET_IDENTITY_MISMATCH')) {
          // Surface the wallet's actual name + url from the guard so
          // the operator knows WHICH extension is masquerading.
          msg = rawMessage.replace('WALLET_IDENTITY_MISMATCH: ', '');
        } else if (rawMessage.startsWith('WALLET_PROVIDER_MISMATCH')) {
          msg = 'The connected wallet changed. Please reconnect and try again.';
        } else {
          msg = detail || 'Wallet verification required. Please sign the message.';
        }
        toast.error(msg);
        setAuthenticatedWallet(null);
        lastPromptedWallet.current = null;
        try {
          disconnect();
        } catch (_) { /* ignore */ }
        // RTL-WALLET-SWITCH — on mismatch, also clear the wallet
        // selection so a fresh pick starts from a clean state.
        try {
          if (typeof select === 'function') select(null);
        } catch (_) { /* ignore */ }
      } finally {
        verifyingRef.current = false;
        setIsVerifying(false);
      }
    },
    [wallet, select, disconnect, loadEntitlements]
  );

  const signOut = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      /* ignore — best effort */
    }
    setAuthenticatedWallet(null);
    setEntitlements(null);
    setCapabilities([]);
    setBenefits(null);
    setQualifyingAssets([]);
    lastPromptedWallet.current = null;
    try {
      disconnect();
    } catch (_) { /* ignore */ }
    // RTL-WALLET-SWITCH (2026-08-24) — clear wallet-adapter selection
    // state so a subsequent provider pick doesn't inherit a stale
    // signer. Without this, the wallet modal remembers the previous
    // choice and can auto-connect the wrong adapter on next reconnect.
    try {
      if (typeof select === 'function') select(null);
    } catch (_) { /* ignore */ }
  }, [disconnect, select]);

  useEffect(() => {
    // RTL-WALLET-LIFECYCLE — Suppress the global MC SIWS auto-effect
    // on routes that own an independent signature flow. See
    // `AUTO_SIWS_SUPPRESSED_PREFIXES` at module scope for details.
    if (typeof window !== 'undefined' &&
        isAutoSiwsSuppressedForPath(window.location.pathname)) {
      return;
    }
    if (!connected || !walletAddress || !signMessage) {
      if (!connected) {
        setAuthenticatedWallet(null);
        lastPromptedWallet.current = null;
      }
      return;
    }

    if (authenticatedWallet === walletAddress) return;

    // Prevent duplicate prompts for the same wallet
    if (lastPromptedWallet.current === walletAddress) return;
    lastPromptedWallet.current = walletAddress;

    (async () => {
      // Try existing server session first — cookie survives page refresh.
      const hadSession = await probeExistingSession(walletAddress);
      if (hadSession) return;
      await requestSignature(walletAddress);
    })();
  }, [
    connected,
    walletAddress,
    signMessage,
    authenticatedWallet,
    probeExistingSession,
    requestSignature,
  ]);

  return (
    <WalletAuthContext.Provider
      value={{
        isAuthenticated,
        authenticatedWallet,
        isVerifying,
        entitlements,
        capabilities,
        benefits,
        qualifyingAssets,
        refreshEntitlements,
        signOut,
      }}
    >
      {children}
    </WalletAuthContext.Provider>
  );
};

export default WalletAuthContext;
